import { visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Root, Text } from 'mdast';

// Define the chart node type for the AST
interface ChartNode {
  type: 'chart';
  chartType: 'candlestick' | 'line';
  dataId: string;
  ticker?: string;
  data?: {
    hName: string;
    hProperties: Record<string, string | boolean | undefined>;
  };
}

// Extend the mdast types to include our custom chart node
declare module 'mdast' {
  interface StaticPhyloMap {
    chart: ChartNode;
  }
}

/**
 * Remark plugin to parse chart tags like <chart type="candlestick" data_id="uuid" />
 * and convert them to custom AST nodes that can be rendered by ReactMarkdown
 */
const remarkChart: Plugin<[], Root> = () => {
  return (tree: Root) => {
    // Process HTML nodes (charts are parsed as HTML by ReactMarkdown)
    visit(tree, 'html', (node: { value: string }, index, parent) => {
      const html = node.value;
      // Updated regex to optionally capture ticker attribute
      const chartRegex = /<chart\s+type="(candlestick|line)"\s+data_id="([^"]+)"(?:\s+ticker="([^"]+)")?\s*\/>/g;

      let match;
      const newNodes: Array<{ type: 'html'; value: string } | ChartNode> = [];
      let lastIndex = 0;

      // Find all chart tags in the HTML
      while ((match = chartRegex.exec(html)) !== null) {
        const [fullMatch, chartType, dataId, ticker] = match;
        const matchStart = match.index!;
        const matchEnd = matchStart + fullMatch.length;

        // Add HTML before the chart (if any)
        if (matchStart > lastIndex) {
          const beforeHtml = html.slice(lastIndex, matchStart);
          if (beforeHtml) {
            newNodes.push({
              type: 'html',
              value: beforeHtml
            });
          }
        }

        // Add the chart node
        const chartNode: ChartNode = {
          type: 'chart',
          chartType: chartType as 'candlestick' | 'line',
          dataId,
          ticker,
          data: {
            hName: 'span',
            hProperties: {
              'data-chart': true,
              'data-chart-type': chartType,
              'data-chart-id': dataId,
              'data-chart-ticker': ticker
            }
          }
        };

        newNodes.push(chartNode);
        lastIndex = matchEnd;
      }

      // If we found any charts, replace the HTML node
      if (newNodes.length > 0) {
        // Add any remaining HTML after the last chart
        if (lastIndex < html.length) {
          const afterHtml = html.slice(lastIndex);
          if (afterHtml) {
            newNodes.push({
              type: 'html',
              value: afterHtml
            });
          }
        }

        // Replace the original HTML node with the new nodes
        if (parent && typeof index === 'number') {
          parent.children.splice(index, 1, ...(newNodes as never[]));
        }
      }
    });

    // Also process text nodes for any charts that might appear there
    visit(tree, 'text', (node: Text, index, parent) => {
      const text = node.value;
      // Updated regex to optionally capture ticker attribute
      const chartRegex = /<chart\s+type="(candlestick|line)"\s+data_id="([^"]+)"(?:\s+ticker="([^"]+)")?\s*\/>/g;

      let match;
      const newNodes: Array<Text | ChartNode> = [];
      let lastIndex = 0;

      // Find all chart tags in the text
      while ((match = chartRegex.exec(text)) !== null) {
        const [fullMatch, chartType, dataId, ticker] = match;
        const matchStart = match.index!;
        const matchEnd = matchStart + fullMatch.length;

        // Add text before the chart (if any)
        if (matchStart > lastIndex) {
          const beforeText = text.slice(lastIndex, matchStart);
          if (beforeText) {
            newNodes.push({
              type: 'text',
              value: beforeText
            });
          }
        }

        // Add the chart node
        const chartNode: ChartNode = {
          type: 'chart',
          chartType: chartType as 'candlestick' | 'line',
          dataId,
          ticker,
          data: {
            hName: 'span',
            hProperties: {
              'data-chart': true,
              'data-chart-type': chartType,
              'data-chart-id': dataId,
              'data-chart-ticker': ticker
            }
          }
        };

        newNodes.push(chartNode);
        lastIndex = matchEnd;
      }

      // If we found any charts, replace the text node
      if (newNodes.length > 0) {
        // Add any remaining text after the last chart
        if (lastIndex < text.length) {
          const afterText = text.slice(lastIndex);
          if (afterText) {
            newNodes.push({
              type: 'text',
              value: afterText
            });
          }
        }

        // Replace the original text node with the new nodes
        if (parent && typeof index === 'number') {
          parent.children.splice(index, 1, ...(newNodes as never[]));
        }
      }
    });
  };
};

export default remarkChart;
